<template>
  <div class="teaching-score-input-handle-main"
       v-loading="loading"
       element-loading-text="数据加载中，请稍等...">
    <template v-if="scheduleScore">
      <CollapseLayout class="rules">
        <template #extra>
          <div class="scale">
            <TitleGroup :bar="false">
              <template #title>
                <div class="theme-text">成绩规则设置</div>
              </template>
              <div class="info">
                <div class="item">
                  <TeachingScoreInputHandleConfig :schedule-id="scheduleId"
                                                  :schedule-score="scheduleScore"
                                                  v-on="$listeners">
                    <span>成绩比例：</span>
                    <span>平时成绩&nbsp;{{scheduleScore.usualScoreScale}}%</span>
                    <span>+</span>
                    <span>考试成绩&nbsp;{{scheduleScore.examScoreScale}}%</span>
                    <span>+</span>
                    <span>作业成绩&nbsp;{{scheduleScore.workScoreScale}}%</span>
                    <span>+</span>
                    <span>实践报告成绩&nbsp;{{scheduleScore.reportScoreScale}}%</span>
                    <span>+</span>
                    <span>案例分析成绩&nbsp;{{scheduleScore.caseScoreScale}}%</span>
                    <span>&emsp;</span>
                    <span>综合成绩显示形式：</span>
                    <span>{{finalScoreGrade ? finalScoreGrade.name : '--'}}</span>
                  </TeachingScoreInputHandleConfig>
                </div>
                <div class="item">
                  <span>注：此处信息可由教务管理老师自由设置</span>
                </div>
              </div>
            </TitleGroup>
          </div>
        </template>
        <div class="tip">
          <TitleGroup :bar="false">
            <template #title>
              <span class="theme-text">提示信息</span>
            </template>
            <div class="info">
              <div class="item">
                <span>成绩为0的请填写/，没有参加考试的请填缺考；未设置成绩比例不可录分；作业成绩为同步数据不可编辑。</span>
              </div>
              <template v-if="scheduleScore.scoreGrades">
                <template v-for="(scoreGrade, index) in scheduleScore.scoreGrades">
                  <div v-if="scoreGrade.options && scoreGrade.options.length"
                       :key="'scoreGrade-' + index"
                       class="item">
                    <span>{{scoreGrade.name}}换算：</span>
                    <span>{{scoreGrade.options.map(option => `${option.name}=${option.convert}`).join('、')}}</span>
                  </div>
                </template>
              </template>
            </div>
          </TitleGroup>
        </div>
      </CollapseLayout>
      <div class="input">
        <div class="header">
          <div class="prepend">
            <TeachingScoreInputHandleImport :schedule-id="scheduleId"
                                            @refresh="loadScheduleScore">
              <template #default="{toggleVisible}">
                <el-button size="mini"
                           type="primary"
                           plain
                           @click="toggleVisible">批量导入成绩</el-button>
              </template>
            </TeachingScoreInputHandleImport>
            <TeachingScoreInputHandleFetch v-if="scheduleScore.examScoreScale"
                                           score-type="examScore"
                                           fetch-text="考试成绩数据计算中..."
                                           :schedule-id="scheduleId"
                                           @fetched="result => onScoreFetched(result, '考试成绩')">
              <template #default="{start}">
                <el-button size="mini"
                           type="primary"
                           plain
                           @click="checkStartFetch(start, '考试成绩')">填充考试成绩</el-button>
              </template>
            </TeachingScoreInputHandleFetch>
            <TeachingScoreInputHandleFetch v-if="scheduleScore.workScoreScale"
                                           score-type="workScore"
                                           fetch-text="作业成绩数据计算中..."
                                           :schedule-id="scheduleId"
                                           @fetched="result => onScoreFetched(result, '作业成绩')">
              <template #default="{start}">
                <el-button size="mini"
                           type="primary"
                           plain
                           @click="checkStartFetch(start, '作业成绩')">填充作业成绩</el-button>
              </template>
            </TeachingScoreInputHandleFetch>
            <el-button size="mini"
                       type="warning"
                       plain
                       @click="handleFillBlank">补全空成绩</el-button>
          </div>
          <div class="append">
            <el-input v-model.trim="input.keyword"
                      size="mini"
                      type="text"
                      clearable
                      placeholder="搜索学生学号/姓名"
                      @clear="handleFilter"
                      @keyup.enter.native="handleFilter">
              <template #append>
                <el-button size="mini"
                           type="primary"
                           @click="handleFilter">搜索</el-button>
              </template>
            </el-input>
          </div>
        </div>
        <div class="body">
          <div class="body-cont">
            <DataGrid ref="grid"
                      size="mini"
                      height="auto"
                      border="inner"
                      show-overflow="title"
                      auto-resize>
              <DataGridColumn type="seq"
                              title="序号"
                              width="80"
                              align="center"></DataGridColumn>
              <DataGridColumn title="班级"
                              field="clazz"
                              min-width="100"
                              align="center"
                              sortable></DataGridColumn>
              <DataGridColumn title="学号"
                              field="regNo"
                              min-width="100"
                              align="center"
                              sortable></DataGridColumn>
              <DataGridColumn title="姓名"
                              field="trueName"
                              min-width="100"
                              align="center"
                              sortable>
                <template #default="{row}">
                  <span class="value">
                    <span v-if="row.isSupply"
                          class="is-star"
                          title="补修"></span>
                    <span>{{row.trueName}}</span>
                  </span>
                </template>
              </DataGridColumn>
              <DataGridColumn title="是否初修"
                              field="isFirst"
                              min-width="80"
                              align="center"
                              sortable>
                <template #default="{row}">
                  <span>{{row.isFirst ? '是' : '否'}}</span>
                </template>
              </DataGridColumn>
              <DataGridColumn v-if="scheduleScore.usualScoreScale"
                              key="usualScoreScale"
                              min-width="120"
                              align="center">
                <template #header="{$table}">
                  <TeachingScoreInputTitle label="平时成绩"
                                           clearable
                                           :scale="scheduleScore.usualScoreScale"
                                           @clear="onClearScore($table, 'scoreUsual')"></TeachingScoreInputTitle>
                </template>
                <template #default="{$table, row, rowIndex}">
                  <BizScorePicker v-model="row.scoreUsualLuru"
                                  trigger="focus"
                                  :score-grades="usualScoreGrades"
                                  v-bind="getScoreRule"
                                  @on-input="val => onInputScore(row, 'scoreUsual', val)">
                    <template #default="{onInput, pretty, isFail, isErr}">
                      <el-input :ref="`scoreInput1_${row.id}`"
                                :value="row.scoreUsualLuru"
                                :class="[{'is-fail': isFail}, {'is-err': isErr}]"
                                size="mini"
                                type="text"
                                placeholder="请输入成绩"
                                @input="onInput"
                                @blur="pretty"
                                @keyup.native="handleKeyUp($table, row, rowIndex, 1, $event)"></el-input>
                    </template>
                  </BizScorePicker>
                </template>
              </DataGridColumn>
              <DataGridColumn v-if="scheduleScore.examScoreScale"
                              key="examScoreScale"
                              min-width="120"
                              align="center">
                <template #header="{$table}">
                  <TeachingScoreInputTitle label="考试成绩"
                                           clearable
                                           :scale="scheduleScore.examScoreScale"
                                           @clear="onClearScore($table, 'scoreExam')"></TeachingScoreInputTitle>
                </template>
                <template #default="{$table, row, rowIndex}">
                  <BizScorePicker v-model="row.scoreExamLuru"
                                  trigger="focus"
                                  :score-grades="examScoreGrades"
                                  v-bind="getScoreRule"
                                  @on-input="val => onInputScore(row, 'scoreExam', val)">
                    <template #default="{onInput, pretty, isFail, isErr}">
                      <el-input :ref="`scoreInput2_${row.id}`"
                                :value="row.scoreExamLuru"
                                :class="[{'is-fail': isFail}, {'is-err': isErr}]"
                                size="mini"
                                type="text"
                                placeholder="请输入成绩"
                                @input="onInput"
                                @blur="pretty"
                                @keyup.native="handleKeyUp($table, row, rowIndex, 2, $event)"></el-input>
                    </template>
                  </BizScorePicker>
                </template>
              </DataGridColumn>
              <DataGridColumn v-if="scheduleScore.workScoreScale"
                              key="workScoreScale"
                              min-width="120"
                              align="center">
                <template #header="{$table}">
                  <TeachingScoreInputTitle label="作业成绩"
                                           clearable
                                           :scale="scheduleScore.workScoreScale"
                                           @clear="onClearScore($table, 'scoreWork')"></TeachingScoreInputTitle>
                </template>
                <template #default="{$table, row, rowIndex}">
                  <BizScorePicker v-model="row.scoreWorkLuru"
                                  trigger="focus"
                                  :score-grades="workScoreGrades"
                                  v-bind="getScoreRule"
                                  @on-input="val => onInputScore(row, 'scoreWork', val)">
                    <template #default="{onInput, pretty, isFail, isErr}">
                      <el-input :ref="`scoreInput3_${row.id}`"
                                :value="row.scoreWorkLuru"
                                :class="[{'is-fail': isFail}, {'is-err': isErr}]"
                                size="mini"
                                type="text"
                                placeholder="请输入成绩"
                                @input="onInput"
                                @blur="pretty"
                                @keyup.native="handleKeyUp($table, row, rowIndex, 3, $event)"></el-input>
                    </template>
                  </BizScorePicker>
                </template>
              </DataGridColumn>
              <DataGridColumn v-if="scheduleScore.reportScoreScale"
                              key="reportScoreScale"
                              min-width="120"
                              align="center">
                <template #header="{$table}">
                  <TeachingScoreInputTitle label="实践报告成绩"
                                           clearable
                                           :scale="scheduleScore.reportScoreScale"
                                           @clear="onClearScore($table, 'scoreReport')"></TeachingScoreInputTitle>
                </template>
                <template #default="{$table, row, rowIndex}">
                  <BizScorePicker v-model="row.scoreReportLuru"
                                  trigger="focus"
                                  :score-grades="reportScoreGrades"
                                  v-bind="getScoreRule"
                                  @on-input="val => onInputScore(row, 'scoreReport', val)">
                    <template #default="{onInput, pretty, isFail, isErr}">
                      <el-input :ref="`scoreInput4_${row.id}`"
                                :value="row.scoreReportLuru"
                                :class="[{'is-fail': isFail}, {'is-err': isErr}]"
                                size="mini"
                                type="text"
                                placeholder="请输入成绩"
                                @input="onInput"
                                @blur="pretty"
                                @keyup.native="handleKeyUp($table, row, rowIndex, 4, $event)"></el-input>
                    </template>
                  </BizScorePicker>
                </template>
              </DataGridColumn>
              <DataGridColumn v-if="scheduleScore.caseScoreScale"
                              key="caseScoreScale"
                              min-width="120"
                              align="center">
                <template #header="{$table}">
                  <TeachingScoreInputTitle label="案例分析成绩"
                                           clearable
                                           :scale="scheduleScore.caseScoreScale"
                                           @clear="onClearScore($table, 'scoreCase')"></TeachingScoreInputTitle>
                </template>
                <template #default="{$table, row, rowIndex}">
                  <BizScorePicker v-model="row.scoreCaseLuru"
                                  trigger="focus"
                                  :score-grades="caseScoreGrades"
                                  v-bind="getScoreRule"
                                  @on-input="val => onInputScore(row, 'scoreCase', val)">
                    <template #default="{onInput, pretty, isFail, isErr}">
                      <el-input :ref="`scoreInput5_${row.id}`"
                                :value="row.scoreCaseLuru"
                                :class="[{'is-fail': isFail}, {'is-err': isErr}]"
                                size="mini"
                                type="text"
                                placeholder="请输入成绩"
                                @input="onInput"
                                @blur="pretty"
                                @keyup.native="handleKeyUp($table, row, rowIndex, 5, $event)"></el-input>
                    </template>
                  </BizScorePicker>
                </template>
              </DataGridColumn>
              <DataGridColumn title="综合成绩"
                              min-width="120"
                              align="center">
                <template #default="{row}">
                  <TeachingScoreInputMerge :score-rule="getScoreRule"
                                           :elective="row"
                                           :score-grade="finalScoreGrade"></TeachingScoreInputMerge>
                </template>
              </DataGridColumn>
            </DataGrid>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <Hint v-if="!loading"
            type="error">
        <div>课程成绩加载失败</div>
        <el-button type="text"
                   @click="loadScheduleScore">重新加载</el-button>
      </Hint>
    </template>
  </div>
</template>

<script>
import CollapseLayout from '../../../../../../../widgets/custom/CollapseLayout'
import TitleGroup from '../../../../../../../widgets/custom/TitleGroup'
import TeachingScoreInputHandleConfig from './config'
import TeachingScoreInputHandleImport from './import'
import TeachingScoreInputHandleFetch from './fetch'
import DataGrid from '../../../../../../../elements/DataGrid'
import DataGridColumn from '../../../../../../../elements/DataGridColumn'
import TeachingScoreInputTitle from '../widget/title'
import TeachingScoreInputMerge from '../widget/merge'
import BizScorePicker from '../../../../../../../biz/picker/score'
import Hint from '../../../../../../../elements/Hint'
import objectUtils from '../../../../../../../../util/objectUtils'
import scoreGradeUtils from '../../../../../../../../util/biz/scoreGradeUtils'
import scoreComputeUtils from '../../../../../../../../util/biz/scoreComputeUtils'
export default {
  name: 'TeachingScoreInputHandleMain',
  inject: {
    $popper: {
      default: null
    }
  },
  components: {
    CollapseLayout,
    TitleGroup,
    TeachingScoreInputHandleConfig,
    TeachingScoreInputHandleImport,
    TeachingScoreInputHandleFetch,
    DataGrid,
    DataGridColumn,
    TeachingScoreInputTitle,
    TeachingScoreInputMerge,
    BizScorePicker,
    Hint
  },
  props: {
    scheduleId: {
      type: String,
      required: true
    },
    visible: Boolean
  },
  data () {
    return {
      loading: false,
      scheduleScore: null,
      input: {
        keyword: ''
      },
      check: {
        keyword: ''
      }
    }
  },
  computed: {
    usualScoreGrades () {
      const scheduleScore = this.scheduleScore
      if (scheduleScore) {
        const usualScoreGrade = scheduleScore.usualScoreGrade
        if (objectUtils.hasValue(usualScoreGrade)) {
          return scoreGradeUtils.getMatched(scheduleScore.scoreGrades, usualScoreGrade)
        }
      }
    },
    examScoreGrades () {
      const scheduleScore = this.scheduleScore
      if (scheduleScore) {
        const examScoreGrade = scheduleScore.examScoreGrade
        if (objectUtils.hasValue(examScoreGrade)) {
          return scoreGradeUtils.getMatched(scheduleScore.scoreGrades, examScoreGrade)
        }
      }
    },
    workScoreGrades () {
      const scheduleScore = this.scheduleScore
      if (scheduleScore) {
        const workScoreGrade = scheduleScore.workScoreGrade
        if (objectUtils.hasValue(workScoreGrade)) {
          return scoreGradeUtils.getMatched(scheduleScore.scoreGrades, workScoreGrade)
        }
      }
    },
    reportScoreGrades () {
      const scheduleScore = this.scheduleScore
      if (scheduleScore) {
        const reportScoreGrade = scheduleScore.reportScoreGrade
        if (objectUtils.hasValue(reportScoreGrade)) {
          return scoreGradeUtils.getMatched(scheduleScore.scoreGrades, reportScoreGrade)
        }
      }
    },
    caseScoreGrades () {
      const scheduleScore = this.scheduleScore
      if (scheduleScore) {
        const caseScoreGrade = scheduleScore.caseScoreGrade
        if (objectUtils.hasValue(caseScoreGrade)) {
          return scoreGradeUtils.getMatched(scheduleScore.scoreGrades, caseScoreGrade)
        }
      }
    },
    finalScoreGrade () {
      const scheduleScore = this.scheduleScore
      if (scheduleScore) {
        const finalScoreGrade = scheduleScore.finalScoreGrade
        if (objectUtils.hasValue(finalScoreGrade)) {
          return scoreGradeUtils.getMatched(scheduleScore.scoreGrades, finalScoreGrade, true)
        }
      }
    },
    getScoreRule () {
      const scheduleScore = this.scheduleScore
      if (scheduleScore) {
        return {
          usualScoreScale: scheduleScore.usualScoreScale,
          examScoreScale: scheduleScore.examScoreScale,
          workScoreScale: scheduleScore.workScoreScale,
          reportScoreScale: scheduleScore.reportScoreScale,
          caseScoreScale: scheduleScore.caseScoreScale,
          scoreExtras: scheduleScore.scoreExtras,
          scoreAlias: scheduleScore.scoreAlias,
          displayFraction: scheduleScore.displayFraction,
          minScore: scheduleScore.minScore,
          maxScore: scheduleScore.maxScore,
          passScore: scheduleScore.passScore
        }
      }
    },
    getElectives () {
      const scheduleScore = this.scheduleScore
      if (scheduleScore) {
        let electives = scheduleScore.electives || []
        let keyword = this.check.keyword
        if (keyword) {
          electives = electives.filter(elective => {
            return (elective.regNo || '').includes(keyword) || (elective.trueName || '').includes(keyword)
          })
        }
        return electives
      }
    }
  },
  methods: {
    handleFilter () {
      Object.keys(this.input).forEach(key => {
        this.check[key] = this.input[key]
      })
    },
    filterResult () {
      const grid = this.$refs.grid
      if (grid) {
        grid.loadData(this.getElectives)
      }
    },
    loadScheduleScore () {
      this.loading = true
      this.$ajax({
        url: '/workspace/manager/teaching/score/input/schedule/score',
        method: 'post',
        data: this.$qs.stringify({
          scheduleId: this.scheduleId
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.scheduleScore = response.data['scheduleScore'] || {}
              this.$nextTick(() => {
                this.filterResult()
                this.$emit('ready')
              })
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.loading = false
      })
    },
    onInputScore (elective, fieldType, val) {
      this.$set(elective, `${fieldType}Zh`, val)
    },
    onClearScore ($table, fieldType) {
      const {fullData} = $table.getTableData()
      if (fullData && fullData.length) {
        fullData.forEach(elective => {
          this.$set(elective, `${fieldType}Luru`, '')
          this.$set(elective, `${fieldType}Zh`, null)
        })
      }
    },
    checkStartFetch (startFetch, scoreType) {
      this.$confirm(`确定要填充${scoreType}吗，已填写成绩将会被修改？`, '提示', {
        confirmButtonText: '继续',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        startFetch()
      }).catch(() => {})
    },
    onScoreFetched (result, scoreType) {
      if (result) {
        const scheduleScore = this.scheduleScore
        if (scheduleScore) {
          let electives = scheduleScore.electives || []
          if (electives.length) {
            let fetchCount = 0
            electives.forEach(elective => {
              let scoreWork = result[elective.id]
              if (scoreWork) {
                Object.keys(scoreWork).forEach(key => {
                  this.$set(elective, key, scoreWork[key])
                })
                fetchCount++
              }
            })
            if (fetchCount) {
              this.$message.success(`成功导入${fetchCount}行${scoreType}数据`)
            } else {
              this.$message.warning(`没有可导入的${scoreType}`)
            }
          }
        }
      }
    },
    handleFillBlank () {
      const scheduleScore = this.scheduleScore
      if (scheduleScore) {
        this.$confirm('确定要补全空白成绩吗？', '提示', {
          confirmButtonText: '继续',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          let electives = scheduleScore.electives || []
          if (electives.length) {
            let scoreCodes = scheduleScore.scoreCodes || []
            if (scoreCodes.length) {
              const fillScore = 0
              const fillScoreLuru = (scheduleScore.scoreAlias || {})[`${fillScore}`] || `${fillScore}`
              scoreCodes.forEach(scoreCode => {
                let scoreScale = scheduleScore[`${scoreCode.code}Scale`]
                if (scoreScale) {
                  electives.forEach(elective => {
                    if (!elective[`${scoreCode.prefix}Luru`]) {
                      this.$set(elective, `${scoreCode.prefix}Luru`, fillScoreLuru)
                      this.$set(elective, `${scoreCode.prefix}Zh`, fillScore)
                    }
                  })
                }
              })
            }
          }
        }).catch(() => {})
      }
    },
    handleElectives (strict) {
      return new Promise((resolve, reject) => {
        const scheduleScore = this.scheduleScore
        if (scheduleScore) {
          let electives = scheduleScore.electives || []
          scoreComputeUtils.validateScore(this.getScoreRule, electives, {
            strict,
            scoreThreshold: 58
          }).then(result => resolve(result))
            .catch(err => reject(err))
        } else {
          reject(new Error('成绩数据未加载'))
        }
      })
    },
    handleKeyUp ($table, elective, rowIndex, scoreIndex, event) {
      switch (event.key) {
        case 'ArrowRight': {
          while (scoreIndex < 5) {
            scoreIndex++
            let scoreInput = this.$refs[`scoreInput${scoreIndex}_${elective.id}`]
            if (scoreInput) {
              this.handleFocusInput(scoreInput)
              break
            }
          }
          break
        }
        case 'ArrowDown': {
          const {fullData} = $table.getTableData()
          if (fullData) {
            let next = fullData[rowIndex + 1]
            if (next) {
              let grid = this.$refs.grid
              if (grid) {
                grid.scrollToRow(next).then(() => {
                  let scoreInput = this.$refs[`scoreInput${scoreIndex}_${next.id}`]
                  if (scoreInput) {
                    this.handleFocusInput(scoreInput)
                  }
                })
              }
            }
          }
          break
        }
        case 'ArrowLeft': {
          while (scoreIndex > 0) {
            scoreIndex--
            let scoreInput = this.$refs[`scoreInput${scoreIndex}_${elective.id}`]
            if (scoreInput) {
              this.handleFocusInput(scoreInput)
              break
            }
          }
          break
        }
        case 'ArrowUp': {
          const {fullData} = $table.getTableData()
          if (fullData) {
            let prev = fullData[rowIndex - 1]
            if (prev) {
              let grid = this.$refs.grid
              if (grid) {
                grid.scrollToRow(prev).then(() => {
                  let scoreInput = this.$refs[`scoreInput${scoreIndex}_${prev.id}`]
                  if (scoreInput) {
                    this.handleFocusInput(scoreInput)
                  }
                })
              }
            }
          }
          break
        }
      }
    },
    handleFocusInput (scoreInput) {
      const input = scoreInput.$refs.input
      if (input) {
        input.focus()
      }
    },
    initialScoreInput () {
      const grid = this.$refs.grid
      if (grid) {
        grid.loadData(null)
      }
    },
    bindPopperEvent () {
      if (this.$popper) {
        this.$popper.$on('closed', this.initialScoreInput)
      }
    },
    unbindPopperEvent () {
      if (this.$popper) {
        this.$popper.$off('closed', this.initialScoreInput)
      }
    }
  },
  created () {
    this.bindPopperEvent()
  },
  mounted () {
    this.loadScheduleScore()
  },
  beforeDestroy () {
    this.unbindPopperEvent()
  },
  watch: {
    visible (val) {
      if (val) {
        this.loadScheduleScore()
      }
    },
    check: {
      handler () {
        this.$nextTick(() => this.filterResult())
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
  @import "../../../../../../../../styles/color";
  .teaching-score-input-handle-main {
    height: 100%;
    display: flex;
    flex-direction: column;
    & > .rules {
      flex: none;
      .scale {
        padding: 15px 20px 15px 8px;
      }
      .tip {
        padding: 10px 20px 20px 8px;
        background-color: #f9f9fa;
      }
      .title-group {
        & > .main {
          margin-top: 8px;
          color: #303133;
          font-size: 12px;
          .info {
            & > .item {
              & + .item {
                margin-top: 4px;
              }
            }
          }
        }
      }
    }
    & > .input {
      flex: auto;
      display: flex;
      flex-direction: column;
      & > .header {
        flex: none;
        display: flex;
        align-items: center;
        & > .prepend {
          flex: auto;
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          padding: 7px 8px;
          .el-button {
            margin: 5px 6px;
            padding: 6px 15px 6px 10px;
            i {
              font-size: 14px;
              vertical-align: -1px;
            }
          }
        }
        & > .append {
          margin-left: 15px;
          padding: 10px 15px 10px 0;
          .el-input {
            width: 220px;
            .el-input__inner {
              border-top-left-radius: 20px;
              border-bottom-left-radius: 20px;
            }
            .el-input-group__append {
              border-top-right-radius: 20px;
              border-bottom-right-radius: 20px;
              padding-left: 15px;
              padding-right: 15px;
            }
          }
        }
      }
      & > .body {
        flex: auto;
        position: relative;
        & > .body-cont {
          position: absolute;
          left: 0;
          top: 0;
          right: 0;
          bottom: 0;
          .vxe-table {
            .vxe-table--header-wrapper {
              .vxe-table--header {
                .vxe-header--row {
                  background-color: #f9f9fa;
                  .vxe-header--column {
                    height: 46px;
                    line-height: 18px;
                    padding: 0;
                    .vxe-cell {
                      padding: 0;
                    }
                  }
                }
              }
            }
            .vxe-table--body-wrapper {
              .vxe-table--body {
                .vxe-body--row {
                  .vxe-body--column {
                    height: 46px;
                    padding: 0;
                    .vxe-cell {
                      .el-input {
                        width: 100px;
                        max-width: 100%;
                        &.is-fail {
                          .el-input__inner {
                            color: $set-danger-color;
                          }
                        }
                        &.is-err {
                          .el-input__inner {
                            border-color: $set-danger-color;
                          }
                        }
                        .el-input__inner {
                          font-weight: bold;
                          text-align: center;
                          &::-webkit-input-placeholder {
                            font-weight: normal;
                          }
                          &:-moz-placeholder {
                            font-weight: normal;
                          }
                          &:-ms-input-placeholder {
                            font-weight: normal;
                          }
                        }
                      }
                      .value {
                        & > .is-star {
                          position: relative;
                          cursor: pointer;
                          &:before {
                            content: '\2605';
                            position: absolute;
                            left: -14px;
                            color: $set-danger-color;
                          }
                        }
                        &.is-fail {
                          color: $set-danger-color;
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
</style>
